import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Grid, MenuItem, Typography, CircularProgress, Alert } from "@mui/material";
import ReactHookFormSelect from "../../../../components/ReactHookFormSelect";
import CustomFileField from "../../../../components/CustomFileField";
import CustomTextField from "../../../../components/CustomTextField";
import KycDocuments from "../../../caf_form/components/KycDocuments";
import useCafForm from "../../../../hooks/useCafForm";
import { ID_PROOF_TYPES, ADDRESS_PROOF_TYPES } from "../../../../constants/kycDocumentTypes";

function UserKycTab() {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();
  const { getCafForm, cafForm } = useCafForm();

  const caf = watch("caf");
  const createCaf = watch("createCaf");
  const isCafLinked = !!caf?.value;

  useEffect(() => {
    if (caf?.value) {
      getCafForm(caf.value);
    }
  }, [caf?.value]);

  if (isCafLinked && !cafForm?.cafId) {
    return <CircularProgress />;
  }

  if (isCafLinked) {
    return <KycDocuments readOnly />;
  }

  return (
    <Grid container spacing={12} className="row" pt={8}>
      {createCaf && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 4 }}>
            {t("kyc_required_for_caf", "KYC documents are required when 'Create CAF' is enabled.")}
          </Alert>
        </Grid>
      )}

      {/* Identity Proof */}
      <Grid item xs={8.5} sm={4.5} md={6} lg={6} xl={6}>
        <Typography variant="h6" sx={{ mb: 8 }}>
          {t("identity_proof", "Identity Proof")}
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <ReactHookFormSelect
              name="idProofType"
              label={t("identity_proof_type", "Identity Proof Type")}
              control={control}
              fullWidth
              variant="outlined"
              errors={errors}
            >
              {ID_PROOF_TYPES.map((type) => (
                <MenuItem key={type.key} value={type.key}>
                  {type.label}
                </MenuItem>
              ))}
            </ReactHookFormSelect>
          </Grid>

          <Grid item xs={12}>
            <CustomTextField
              label={t("id_proof_number", "ID Proof Number")}
              name="idProofNumber"
              control={control}
              fullWidth
              variant="outlined"
              errors={errors}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomFileField
              name="idProofFile"
              label={t("upload_identity_proof", "Upload Identity Proof")}
              control={control}
              errors={errors}
              accept="application/pdf,image/*"
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Address Proof */}
      <Grid item xs={8.5} sm={4.5} md={6} lg={6} xl={6}>
        <Typography variant="h6" sx={{ mb: 8 }}>
          {t("address_proof", "Address Proof")}
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <ReactHookFormSelect
              name="addressProofType"
              label={t("address_proof_type", "Address Proof Type")}
              control={control}
              fullWidth
              variant="outlined"
              errors={errors}
            >
              {ADDRESS_PROOF_TYPES.map((type) => (
                <MenuItem key={type.key} value={type.key}>
                  {type.label}
                </MenuItem>
              ))}
            </ReactHookFormSelect>
          </Grid>

          <Grid item xs={12}>
            <CustomTextField
              label={t("address_proof_number", "Address Proof Number")}
              name="addressProofNumber"
              control={control}
              fullWidth
              variant="outlined"
              errors={errors}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomFileField
              name="addressProofFile"
              label={t("upload_address_proof", "Upload Address Proof")}
              control={control}
              errors={errors}
              accept="application/pdf,image/*"
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Profile Picture */}
      <Grid item xs={8.5} sm={4.5} md={6} lg={6} xl={6}>
        <Typography variant="h6" sx={{ mb: 6 }}>
          {t("profile_picture", "Profile Picture")}
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <CustomFileField
              name="profileProofFile"
              label={t("upload_profile_picture", "Upload Profile Picture")}
              control={control}
              errors={errors}
              accept="application/pdf,image/*"
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default UserKycTab;
